import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, from, throwError } from 'rxjs';
import { switchMap, finalize, catchError } from 'rxjs/operators';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';
import {
  WithdrawalsListResponse,
  WithdrawalQueryParams,
  ConfirmWithdrawalDto,
  RejectWithdrawalDto,
  WithdrawalDetailResponse,
} from '../interfaces/binance-withdrawal.interface';

export interface WithdrawalRequest {
  userId: string;
  walletAddress: string;
  network: string;
  amount: number;
  fee: number;
}

export interface WithdrawalResponse {
  withdrawalId: string;
  status: string;
  message?: string;
}

export interface WithdrawalHistoryItem {
  id: string;
  userId: string;
  walletAddress: string;
  network: string;
  coin: string;
  requestedAmount: number;
  fee: number;
  netAmount: number;
  status: 'pending' | 'processing' | 'completed' | 'rejected' | 'cancelled';
  binanceTxId?: string;
  rejectionReason?: string;
  requestedAt: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface WithdrawalHistoryResponse {
  items: WithdrawalHistoryItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface BalanceResponse {
  totalBalance: number;
  blockedBalance: number;
  availableBalance: number;
  pendingWithdrawals?: Array<{
    id: string;
    amount: number;
    status: string;
    requestedAt: string;
  }>;
}

export interface UserCardexMovement {
  id: string;
  userId: string;
  txId: string;
  binanceId: string;
  coin: string;
  network: string;
  amount: number;
  description?: string;
  debe: number;
  haber: number;
  saldo: number;
  date: string;
  time: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserCardexMovementsResponse {
  user: {
    userId: number;
    userName: string;
    userEmail: string;
  };
  data: UserCardexMovement[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UserCardexMovementsQuery {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
}

export interface ManualDebitDto {
  userId: string;
  amount: number;
  reason: string;
}

export interface ManualCreditDto {
  userId: string;
  amount: number;
  reason: string;
}

@Injectable({
  providedIn: 'root',
})
export class BinanceWithdrawalService {
  private baseUrl = environment.apiNest;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session?.accessToken?.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }

    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${session.accessToken.jwtToken.trim()}`);
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ha ocurrido un error en el servicio';

    if (error.error instanceof ErrorEvent) {
      // Error del lado del cliente
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Error del lado del servidor
      switch (error.status) {
        case 400:
          // Bad Request - errores de validación o lógica de negocio
          if (error.error?.message) {
            errorMessage = error.error.message;

            // Si hay detalles adicionales (como en saldo insuficiente), agregarlos
            if (error.error?.detalles) {
              const detalles = error.error.detalles;
              if (detalles.saldoDisponible !== undefined && detalles.montoSolicitado !== undefined) {
                errorMessage += `\n\nSaldo disponible: $${detalles.saldoDisponible.toFixed(2)} USDT`;
                errorMessage += `\nMonto solicitado: $${detalles.montoSolicitado.toFixed(2)} USDT`;
              }
            }
          } else {
            errorMessage = 'Datos inválidos. Por favor, verifique la información ingresada.';
          }
          break;
        case 401:
          errorMessage = 'Error de autenticación. Por favor, inicie sesión nuevamente.';
          break;
        case 403:
          errorMessage = 'No tiene permisos para realizar esta acción.';
          break;
        case 404:
          if (error.error?.message) {
            errorMessage = error.error.message;
          } else {
            errorMessage = 'Recurso no encontrado.';
          }
          break;
        case 429:
          // Rate limit excedido - preservar el error completo para el componente
          // El componente manejará el error 429 con su propia lógica
          return throwError(() => error);
        case 500:
          errorMessage = 'Error interno del servidor. Por favor, intente más tarde.';
          break;
        case 503:
          errorMessage = 'Servicio temporalmente no disponible. Por favor, intente más tarde.';
          break;
        default:
          // Intentar obtener el mensaje del error del backend
          errorMessage = error.error?.message || error.message || errorMessage;
      }
    }

    return throwError(() => ({ message: errorMessage, originalError: error }));
  }

  /**
   * Solicita un retiro de USDT
   * @param request - Datos del retiro (userId, walletAddress, network, amount, fee)
   * @returns Observable con la respuesta del retiro
   */
  async requestWithdrawal(
    request: WithdrawalRequest,
  ): Promise<Observable<WithdrawalResponse>> {
    const headers = await this.getHeaders();
    const serviceURL = `${this.baseUrl}binance-withdrawals/request`;

    return new Promise((resolve) => {
      resolve(
        this.http.post<WithdrawalResponse>(serviceURL, request, { headers }),
      );
    });
  }

  /**
   * Obtiene el historial de retiros del usuario con paginación
   * @param page - Número de página (default: 1)
   * @param limit - Cantidad de items por página (default: 10)
   * @param status - Filtrar por estado (opcional)
   * @returns Observable con el historial paginado de retiros
   */
  async getWithdrawalHistory(
    page: number = 1,
    limit: number = 10,
    status?: string,
  ): Promise<Observable<WithdrawalHistoryResponse>> {
    const headers = await this.getHeaders();
    let serviceURL = `${this.baseUrl}binance-withdrawals/my-withdrawals?page=${page}&limit=${limit}`;

    if (status) {
      serviceURL += `&status=${status}`;
    }

    return new Promise((resolve) => {
      resolve(
        this.http.get<WithdrawalHistoryResponse>(serviceURL, { headers }),
      );
    });
  }

  /**
   * Obtiene el balance del usuario (total, bloqueado, disponible)
   * @returns Observable con la información del balance
   */
  async getBalance(): Promise<Observable<BalanceResponse>> {
    const headers = await this.getHeaders();
    const serviceURL = `${this.baseUrl}binance-withdrawals/balance`;

    return new Promise((resolve) => {
      resolve(this.http.get<BalanceResponse>(serviceURL, { headers }));
    });
  }

  // ==================== OPERATOR METHODS ====================

  /**
   * Obtiene todos los retiros (solo para operadores)
   * @param params - Parámetros de consulta (page, limit, status, userId, startDate, endDate)
   * @returns Observable con la lista paginada de retiros
   */
  getAllWithdrawals(
    params: WithdrawalQueryParams = {},
  ): Observable<WithdrawalsListResponse> {
    this.loadingSubject.next(true);

    let httpParams = new HttpParams();
    if (params.page)
      httpParams = httpParams.set('page', params.page.toString());
    if (params.limit)
      httpParams = httpParams.set('limit', params.limit.toString());
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.userId) httpParams = httpParams.set('userId', params.userId);
    if (params.startDate)
      httpParams = httpParams.set('startDate', params.startDate);
    if (params.endDate) httpParams = httpParams.set('endDate', params.endDate);

    const serviceURL = `${this.baseUrl}binance-withdrawals`;

    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<WithdrawalsListResponse>(serviceURL, {
          headers,
          params: httpParams,
        }),
      ),
      finalize(() => this.loadingSubject.next(false)),
      catchError(this.handleError),
    );
  }

  /**
   * Obtiene el detalle de un retiro específico (solo para operadores)
   * @param id - ID del retiro
   * @returns Observable con el detalle del retiro
   */
  getWithdrawalDetail(id: string): Observable<WithdrawalDetailResponse> {
    this.loadingSubject.next(true);

    const serviceURL = `${this.baseUrl}binance-withdrawals/${id}`;

    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<WithdrawalDetailResponse>(serviceURL, { headers }),
      ),
      finalize(() => this.loadingSubject.next(false)),
      catchError(this.handleError),
    );
  }

  /**
   * Confirma/aprueba un retiro (solo para operadores)
   * @param id - ID del retiro
   * @param dto - Datos de confirmación (binanceTxId)
   * @returns Observable con la respuesta de confirmación
   */
  confirmWithdrawal(id: string, dto: ConfirmWithdrawalDto): Observable<any> {
    this.loadingSubject.next(true);

    const serviceURL = `${this.baseUrl}binance-withdrawals/${id}/confirm`;

    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<any>(serviceURL, dto, { headers })),
      finalize(() => this.loadingSubject.next(false)),
      catchError(this.handleError),
    );
  }

  /**
   * Reenvía a Binance un retiro en POR_PROCESAR que ya fue una vez (tercera
   * opción, aparte de aprobar y rechazar).
   */
  resendWithdrawal(id: string): Observable<any> {
    this.loadingSubject.next(true);

    const serviceURL = `${this.baseUrl}binance-withdrawals/${id}/resend`;

    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<any>(serviceURL, {}, { headers })),
      finalize(() => this.loadingSubject.next(false)),
      catchError(this.handleError),
    );
  }

  /**
   * Rechaza un retiro (solo para operadores)
   * @param id - ID del retiro
   * @param dto - Datos de rechazo (reason)
   * @returns Observable con la respuesta de rechazo
   */
  rejectWithdrawal(id: string, dto: RejectWithdrawalDto): Observable<any> {
    this.loadingSubject.next(true);

    const serviceURL = `${this.baseUrl}binance-withdrawals/${id}/reject`;

    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<any>(serviceURL, dto, { headers })),
      finalize(() => this.loadingSubject.next(false)),
      catchError(this.handleError),
    );
  }

  /**
   * Obtiene el balance de un usuario específico (solo para operadores)
   * @param userId - ID del usuario
   * @returns Observable con el balance del usuario (total, bloqueado, disponible)
   */
  getUserBalanceByUserId(userId: string): Observable<BalanceResponse> {
    this.loadingSubject.next(true);

    const serviceURL = `${this.baseUrl}binance-withdrawals/user/${userId}/balance`;

    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<BalanceResponse>(serviceURL, { headers }),
      ),
      finalize(() => this.loadingSubject.next(false)),
      catchError(this.handleError),
    );
  }

  /**
   * Obtiene los movimientos del cardex de un usuario con formato (solo para operadores)
   * @param userId - ID del usuario
   * @param query - Parámetros de consulta (page, limit, startDate, endDate)
   * @returns Observable con los movimientos del cardex formateados
   */
  getUserCardexMovementsFormatted(
    userId: string,
    query: UserCardexMovementsQuery = {},
  ): Observable<UserCardexMovementsResponse> {
    this.loadingSubject.next(true);

    let httpParams = new HttpParams();
    if (query.page) httpParams = httpParams.set('page', query.page.toString());
    if (query.limit)
      httpParams = httpParams.set('limit', query.limit.toString());
    if (query.startDate)
      httpParams = httpParams.set('startDate', query.startDate);
    if (query.endDate) httpParams = httpParams.set('endDate', query.endDate);

    const serviceURL = `${this.baseUrl}binance-withdrawals/user/${userId}/cardex-movements-formatted`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<UserCardexMovementsResponse>(serviceURL, {
          headers,
          params: httpParams,
        }),
      ),
      finalize(() => this.loadingSubject.next(false)),
      catchError(this.handleError),
    );
  }

  /**
   * Realiza un débito manual de saldo de usuario (solo para operadores)
   * @param userId - ID del usuario
   * @param dto - Datos del débito (userId, amount, reason)
   * @returns Observable con la respuesta del débito
   */
  manualDebit(userId: string, dto: ManualDebitDto): Observable<any> {
    this.loadingSubject.next(true);

    const serviceURL = `${this.baseUrl}binance-withdrawals/user/${userId}/manual-debit`;

    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<any>(serviceURL, dto, { headers })),
      finalize(() => this.loadingSubject.next(false)),
      catchError(this.handleError),
    );
  }

  /**
   * Realiza un crédito manual de saldo de usuario (solo para operadores)
   * @param userId - ID del usuario
   * @param dto - Datos del crédito (userId, amount, reason)
   * @returns Observable con la respuesta del crédito
   */
  manualCredit(userId: string, dto: ManualCreditDto): Observable<any> {
    this.loadingSubject.next(true);

    const serviceURL = `${this.baseUrl}binance-withdrawals/user/${userId}/manual-credit`;

    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<any>(serviceURL, dto, { headers })),
      finalize(() => this.loadingSubject.next(false)),
      catchError(this.handleError),
    );
  }
}
